/* eslint-disable react-refresh/only-export-components -- come in `ActiveMatchContext`:
   provider e hook sono la stessa cosa vista dai due lati, e separarli in due file
   metterebbe il contesto in un terzo file che nessuno dei due importa da solo. */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import type { LandingNotice } from '@/lib/landingNotice';

/**
 * ADR-0115 — il canale con cui una superficie di atterraggio DICE che non ce
 * l'ha fatta, senza prendersi lo schermo.
 *
 * ═══ PERCHE' UN CONTESTO E NON UNO STATO DELLA ROTTA ═══
 * Perche' la modale deve sopravvivere alla rotta che l'ha alzata. Il senso
 * dell'ADR e' che chi fallisce l'atterraggio se ne VA (verso una superficie
 * viva) e il messaggio resta: se il messaggio vivesse dentro `InviteLanding`,
 * andarsene lo smonterebbe, e per tenerlo bisognerebbe restare — cioe' la pagina
 * d'errore di prima.
 *
 * ⚠️ Un solo avviso alla volta, e il secondo SOSTITUISCE il primo. Non e' una
 * semplificazione: due modali impilate all'avvio sono due tocchi a vuoto, e
 * l'ADR esiste per togliere il secondo atto, non per aggiungerne uno.
 *
 * 📌 Non c'e' persistenza. Chiuso e' chiuso: v. il blocco «CHIUSA SENZA ESSERE
 * LETTA» in `LandingNoticeModal.tsx`, dove il comportamento e' dichiarato per
 * quello che e'.
 */
export interface LandingNoticeEntry {
  notice: LandingNotice;
  /**
   * Dove porta l'azione `retry`, quando la superficie ne ha una.
   *
   * Sta QUI e non dentro `LandingNotice` perche' solo chi alza l'avviso conosce
   * la risorsa: per l'invito e' `/invito/<codice>`, e riprovare significa
   * ri-atterrare. ⛔ NON e' un `location.reload()`: da qui in poi la rotta e'
   * quella della superficie viva, e ricaricarla ricaricherebbe la Hub — cioe'
   * butterebbe via il codice invece di rileggerlo.
   */
  retryTo?: string;
}

interface LandingNoticeApi {
  entry: LandingNoticeEntry | null;
  raise: (entry: LandingNoticeEntry) => void;
  dismiss: () => void;
}

const Ctx = createContext<LandingNoticeApi | null>(null);

export function LandingNoticeProvider({ children }: { children: ReactNode }) {
  const [entry, setEntry] = useState<LandingNoticeEntry | null>(null);
  const raise = useCallback((next: LandingNoticeEntry) => setEntry(next), []);
  const dismiss = useCallback(() => setEntry(null), []);
  const value = useMemo<LandingNoticeApi>(
    () => ({ entry, raise, dismiss }),
    [entry, raise, dismiss],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * ⚠️ Lancia se manca il provider, invece di degradare a un no-op silenzioso: un
 * avviso di atterraggio che non compare e' indistinguibile dal redirect muto —
 * esattamente il guasto che questo meccanismo esiste per togliere. Meglio un
 * albero rotto in sviluppo che un fallimento invisibile in produzione.
 */
export function useLandingNotice(): LandingNoticeApi {
  const api = useContext(Ctx);
  if (!api) throw new Error('useLandingNotice fuori da <LandingNoticeProvider>');
  return api;
}
